import { Toast } from '@base-ui/react/toast';
import { useEffect } from 'react';
import { useEditorEvent } from '@/editor-hooks/useEditorEvent';
import { EditorEventName } from '@/editor/event/editorEvent';
import { EToastType, EToastVariant, toastManager } from '@/stores/feedback';
import { IconFont } from '@/components/iconfont/iconfont';
import { CustomIconLoading } from '@/components/icons';
import { addToast, bindToastFocusListener, syncToastLimited } from './toast-service';
import styles from './toast.module.scss';

export function ToastRenderer() {
  // 内核经 editorEvent 发出的 toast 与应用层共用同一套默认值与计时
  useEditorEvent(EditorEventName.FEEDBACK_TOAST, addToast);

  useEffect(() => bindToastFocusListener(), []);

  return (
    // timeout 为 0 表示禁用 base-ui 自带的计时器，倒计时由 toast-service 接管
    <Toast.Provider toastManager={toastManager} limit={1} timeout={0}>
      <Toast.Portal>
        {/* data-toast-viewport 是给 global.scss 抬 z-index 用的钩子，见那里的 Portal 层级规则 */}
        <Toast.Viewport className={styles.toastViewport} data-toast-viewport>
          <ToastList />
        </Toast.Viewport>
      </Toast.Portal>
    </Toast.Provider>
  );
}

function ToastList() {
  const { toasts } = Toast.useToastManager();

  useEffect(() => {
    syncToastLimited(toasts);
  }, [toasts]);

  // data-has-action 给按钮多开一列：Toast.Action 没有文案时整个元素不渲染，
  // 无条件留那一列会让所有 toast 都多出一段列间距
  return toasts.map((toast) => (
    <Toast.Root
      key={toast.id}
      toast={toast}
      className={styles.toast}
      data-notification-variant={toast.data?.variant ?? EToastVariant.Toast}
      data-has-action={toast.actionProps ? '' : undefined}
      swipeDirection={['up']}
    >
      <Toast.Title
        className={styles.title}
        render={(props, state) => (
          <div {...props}>
            {state.type === EToastType.SUCCESS && (
              <IconFont iconName="icon-icon_normal_success_fill_24" className={styles.icon} />
            )}
            {state.type === EToastType.ERROR && (
              <IconFont iconName="icon-icon_normal_error_fill_24" className={styles.icon} />
            )}
            {state.type === EToastType.WARNING && (
              <IconFont iconName="icon-icon_normal_warning_fill_24" className={styles.icon} />
            )}
            {state.type === EToastType.REGULAR && (
              <IconFont iconName="icon-icon_normal_tip_fill_24" className={styles.icon} />
            )}
            {state.type === EToastType.LOADING && <CustomIconLoading className={styles.icon} data-loading="true" />}
            <div>{toast.title}</div>
          </div>
        )}
      />
      {toast.description && <Toast.Description className={styles.description} />}
      <Toast.Action className={styles.action} />
    </Toast.Root>
  ));
}
